/**
 * Feed the active regime's terms to every translated string (P415).
 *
 * WHY NOT PER CALL SITE. The catalogue carries `{{serviceTerm}}` in
 * roughly 190 strings rendered from about eighty components. Passing
 * the term at each `t()` would be eighty edits to make and eighty
 * places to forget, and a forgotten one renders the placeholder
 * literally -- "the {{serviceTerm}} failed" on a screen. i18next's
 * `interpolation.defaultVariables` supplies them once, so a string can
 * use a term without its caller knowing the term exists.
 *
 * THE FALLBACK, and why it is not the cross-regime fallback this track
 * refuses elsewhere. A string must render something: there is no
 * caller here to decide, the way `useRegimeVocab` lets one decide.
 * When the bank has not recorded a regime the terms default to SAMA's,
 * which is EXACTLY what the product renders today -- those words are
 * already in the catalogue as literals. So an unrecorded deployment
 * sees no change at all, which is the honest outcome; it is the status
 * quo preserved, not a regime asserted. The moment a regime is
 * recorded, its own terms win.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { REGIME_VOCAB, regimeVocab } from "shared/i18n/regime";
import { useRegime } from "shared/regime/useRegime";

/** Every scalar term, flattened for interpolation. */
function termsOf(vocab: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(vocab)) {
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

/**
 * What the catalogue's literals already said. Not a choice about which
 * regulator a bank answers to -- a statement of what the strings read
 * before they became interpolations.
 */
const TERMS_BEFORE_THIS_PHASE = termsOf(
  REGIME_VOCAB.sama?.en as unknown as Record<string, unknown>,
);

export function RegimeTerms({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const { regime } = useRegime();

  useEffect(() => {
    const vocab = regimeVocab(regime?.code ?? null, i18n.language);
    const terms = vocab
      ? termsOf(vocab as unknown as Record<string, unknown>)
      : TERMS_BEFORE_THIS_PHASE;
    // Mutating the live options rather than re-initialising: an
    // `i18n.init` here would drop the language the user chose and
    // remount every subscriber.
    const interpolation = i18n.options.interpolation ?? {};
    i18n.options.interpolation = { ...interpolation, defaultVariables: terms };
    // Nothing re-renders on an options change, so nudge the
    // subscribers the way a language switch does.
    i18n.emit("languageChanged", i18n.language);
  }, [i18n, regime?.code, i18n.language]);

  return <>{children}</>;
}
